import { z } from "zod";
import { opaqueIdSchema, sha256Schema } from "./opaque.js";
export const localeSchema = z
    .string()
    .min(2)
    .max(35)
    .regex(/^[A-Za-z]{2,3}(?:-[A-Za-z]{4})?(?:-(?:[A-Za-z]{2}|\d{3}))?(?:-[A-Za-z0-9]{5,8})*$/)
    .refine((locale) => {
    try {
        return Intl.getCanonicalLocales(locale)[0] === locale;
    }
    catch {
        return false;
    }
}, "Expected a canonical, bounded BCP-47 locale");
export const translationSourceKindSchema = z.enum([
    "BOOK_METADATA",
    "CHAPTER_REVISION",
    "CAPTION_REVISION",
]);
export const immutableSourceRevisionSchema = z
    .object({
    sourceRevisionId: opaqueIdSchema,
    bookId: opaqueIdSchema,
    sourceEntityId: opaqueIdSchema,
    sourceKind: translationSourceKindSchema,
    sourceLocale: localeSchema,
    checksumSha256: sha256Schema,
    immutable: z.literal(true),
    createdAt: z.string().datetime(),
})
    .strict();
export const translationProviderModeSchema = z.enum([
    "HUMAN",
    "DETERMINISTIC",
    "SANDBOX",
    "UNAVAILABLE",
]);
export const translationApprovalStateSchema = z.enum(["PENDING", "APPROVED", "CHANGES_REQUESTED"]);
export const translationConsentStateSchema = z.enum(["GRANTED", "REVOKED"]);
export const translationConsentSchema = z
    .object({
    consentId: opaqueIdSchema,
    bookId: opaqueIdSchema,
    grantedBy: opaqueIdSchema,
    sourceLocale: localeSchema,
    targetLocale: localeSchema,
    state: translationConsentStateSchema,
    grantedAt: z.string().datetime(),
    revokedAt: z.string().datetime().nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.sourceLocale === value.targetLocale)
        context.addIssue({
            code: "custom",
            path: ["targetLocale"],
            message: "Translation target must differ from the source locale.",
        });
    if (value.state === "REVOKED" && value.revokedAt === null)
        context.addIssue({ code: "custom", path: ["revokedAt"], message: "Required when revoked." });
    if (value.state === "GRANTED" && value.revokedAt !== null)
        context.addIssue({
            code: "custom",
            path: ["revokedAt"],
            message: "Only valid when revoked.",
        });
});
export const translationRevisionSchema = z
    .object({
    translationRevisionId: opaqueIdSchema,
    sourceRevisionId: opaqueIdSchema,
    parentRevisionId: opaqueIdSchema.nullable(),
    targetLocale: localeSchema,
    revision: z.number().int().positive(),
    checksumSha256: sha256Schema,
    providerMode: translationProviderModeSchema,
    providerEvidenceId: opaqueIdSchema.nullable(),
    consentId: opaqueIdSchema,
    approvalState: translationApprovalStateSchema,
    approvedBy: opaqueIdSchema.nullable(),
    approvedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    immutable: z.literal(true),
})
    .strict()
    .superRefine((value, context) => {
    if (value.providerMode === "UNAVAILABLE" && value.providerEvidenceId !== null)
        context.addIssue({
            code: "custom",
            path: ["providerEvidenceId"],
            message: "Unavailable mode cannot claim provider evidence.",
        });
    const approved = value.approvalState === "APPROVED";
    if (approved !== (value.approvedBy !== null && value.approvedAt !== null))
        context.addIssue({
            code: "custom",
            path: ["approvalState"],
            message: "Human approval identity and time must agree with approval state.",
        });
});
export const readingModeSchema = z.enum([
    "STANDARD",
    "LARGE_PRINT",
    "HIGH_CONTRAST",
    "DYSLEXIA_FRIENDLY",
    "SCREEN_READER",
]);
export const readingPreferencesSchema = z
    .object({
    userId: opaqueIdSchema,
    locale: localeSchema,
    readingMode: readingModeSchema,
    fontFamily: z.enum(["SERIF", "SANS_SERIF", "DYSLEXIA_FRIENDLY"]),
    fontScale: z.number().min(0.8).max(2),
    lineHeight: z.number().min(1.2).max(2.4),
    contrast: z.enum(["SYSTEM", "STANDARD", "HIGH"]),
    motion: z.enum(["SYSTEM", "FULL", "REDUCED"]),
    captions: z.enum(["AUTO", "ON", "OFF"]),
    transcripts: z.enum(["AUTO", "ON", "OFF"]),
    updatedAt: z.string().datetime(),
})
    .strict();
export const bookKindSchema = z.enum([
    "LIFE_STORY",
    "COOKBOOK",
    "BABY",
    "WEDDING",
    "TRAVEL",
    "TRIBUTE",
    "BEDTIME",
]);
export const capabilityAvailabilitySchema = z.enum(["WORKING", "SANDBOX", "ROADMAP"]);
const semanticTemplateIdSchema = z
    .string()
    .min(5)
    .max(100)
    .regex(/^[a-z][a-z0-9]*(?:-[a-z0-9]+)+$/, "Expected a bounded semantic template ID");
export const bookTemplateIdSchema = semanticTemplateIdSchema;
export const visualStyleIdSchema = semanticTemplateIdSchema;
export const bookTypeProfileSchema = z
    .object({
    bookId: opaqueIdSchema,
    kind: bookKindSchema,
    locale: localeSchema,
    templateId: bookTemplateIdSchema,
    visualStyleId: visualStyleIdSchema,
    availability: capabilityAvailabilitySchema,
    updatedAt: z.string().datetime(),
})
    .strict();
export const bookTypeCatalogEntrySchema = z
    .object({
    kind: bookKindSchema,
    availability: capabilityAvailabilitySchema,
    templateIds: z.array(bookTemplateIdSchema).min(1).max(50),
    visualStyleIds: z.array(visualStyleIdSchema).min(1).max(50),
    supportedLocales: z.array(localeSchema).min(1).max(50),
})
    .strict();
//# sourceMappingURL=localization-accessibility.js.map